import React, { Fragment } from 'react';
import { ShieldCheck, ShieldAlert, AlertTriangle, ArrowRight, RefreshCw } from 'lucide-react';
interface ScanResultCardProps {
  score?: number;
  status: 'completed' | 'failed';
  errorMessage?: string;
  onViewReport?: () => void;
  onBack?: () => void;
  onRetry?: () => void;
}
export function ScanResultCard({
  score = 0,
  status,
  errorMessage,
  onViewReport,
  onBack,
  onRetry
}: ScanResultCardProps) {
  const isSuccess = status === 'completed';
  let themeColor = '#FF4B4B'; // Default to red for failed
  let riskLabel = 'High Risk\nDetected';
  let Icon = ShieldAlert;
  if (isSuccess) {
    if (score <= 30) {
      themeColor = '#00C18E';
      riskLabel = 'Low Risk\nDetected';
      Icon = ShieldCheck;
    } else if (score <= 70) {
      themeColor = '#FFB800';
      riskLabel = 'Moderate Risk\nDetected';
      Icon = AlertTriangle;
    } else {
      themeColor = '#FF4B4B';
      riskLabel = 'High Risk\nDetected';
      Icon = ShieldAlert;
    }
  }
  return <div className="relative w-full max-w-md bg-[#050505] overflow-hidden p-8 sm:p-10" style={{
    '--theme-color': themeColor,
    '--theme-color-15': `${themeColor}15`,
    '--theme-color-20': `${themeColor}20`,
    border: '1px solid var(--theme-color)',
    boxShadow: '0 0 30px var(--theme-color-15)'
  } as React.CSSProperties} data-id="element-1655">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none z-0" data-id="element-1656" />

      {/* Corner Brackets */}
      <div className="absolute top-0 left-0 w-5 h-5 border-t-4 border-l-4 z-10" style={{
      borderColor: themeColor
    }} data-id="element-1657" />
      <div className="absolute top-0 right-0 w-5 h-5 border-t-4 border-r-4 z-10" style={{
      borderColor: themeColor
    }} data-id="element-1658" />
      <div className="absolute bottom-0 left-0 w-5 h-5 border-b-4 border-l-4 z-10" style={{
      borderColor: themeColor
    }} data-id="element-1659" />
      <div className="absolute bottom-0 right-0 w-5 h-5 border-b-4 border-r-4 z-10" style={{
      borderColor: themeColor
    }} data-id="element-1660" />

      <div className="relative z-10 flex flex-col items-center text-center" data-id="element-1661">
        {/* Icon */}
        <div className="mb-8 flex justify-center" data-id="element-1662">
          <div className="p-5 rounded-full border-2 bg-[#050505]" style={{
          borderColor: 'var(--theme-color)',
          boxShadow: 'inset 0 0 20px var(--theme-color-20)'
        }} data-id="element-1663">
            <Icon size={48} color={themeColor} strokeWidth={1.5} data-id="element-1664" />
          </div>
        </div>

        {isSuccess ? <>
            <h2 className="text-2xl sm:text-3xl font-black text-white uppercase tracking-tight mb-8" data-id="element-1665">
              Scan Completed
            </h2>

            <div className="w-full py-6 border-y border-gray-800/60 mb-8 flex items-center justify-center gap-6" data-id="element-1666">
              <div className="text-right flex-1" data-id="element-1667">
                <p className="text-[10px] sm:text-xs text-gray-500 font-mono uppercase tracking-widest mb-2" data-id="element-1668">
                  Toxicity Score
                </p>
                <p className="text-4xl sm:text-5xl font-black tracking-tighter" style={{
              color: themeColor
            }} data-id="element-1669">
                  {score}/100
                </p>
              </div>
              <div className="h-16 w-px bg-gray-800/60" data-id="element-1670" />
              <div className="text-left flex-1" data-id="element-1671">
                <p className="text-[10px] sm:text-xs text-gray-500 font-mono uppercase tracking-widest mb-2" data-id="element-1672">
                  Assessment
                </p>
                <p className="text-lg sm:text-xl font-bold leading-snug" style={{
              color: themeColor
            }} data-id="element-1673">
                  {riskLabel.split('\n').map((line, i) => <Fragment key={i} data-id="element-1674">
                      {line}
                      {i === 0 && <br data-id="element-1675" />}
                    </Fragment>)}
                </p>
              </div>
            </div>

            <div className="w-full space-y-5" data-id="element-1676">
              <button onClick={onViewReport} className="w-full py-4 font-bold text-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]" style={{
            backgroundColor: themeColor
          }} data-id="element-1677">
                View Full Report
                <ArrowRight size={20} data-id="element-1678" />
              </button>

              <button onClick={onBack} className="w-full py-2 text-gray-500 hover:text-white font-mono text-xs uppercase tracking-[0.2em] transition-colors" data-id="element-1679">
                Back to Dashboard
              </button>
            </div>
          </> : <>
            <h2 className="text-2xl sm:text-3xl font-black text-white uppercase tracking-tight mb-4" data-id="element-1680">
              Scan Failed
            </h2>

            <p className="text-gray-400 font-mono text-sm mb-10 max-w-[250px] mx-auto leading-relaxed" data-id="element-1681">
              {errorMessage || 'We encountered an error while processing your conversation data.'}
            </p>

            <div className="w-full space-y-5" data-id="element-1682">
              <button onClick={onRetry} className="w-full py-4 font-bold text-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]" style={{
            backgroundColor: themeColor
          }} data-id="element-1683">
                <RefreshCw size={20} data-id="element-1684" />
                Try Again
              </button>

              <button onClick={onBack} className="w-full py-2 text-gray-500 hover:text-white font-mono text-xs uppercase tracking-[0.2em] transition-colors" data-id="element-1685">
                Back to Dashboard
              </button>
            </div>
          </>}
      </div>
    </div>;
}